import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { getPull } from '@mkcore/dev/schema';
import { Badge, Card, ErrorState, Mono, PageHeader, Skeleton } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { Markdown } from '@/components/markdown';
import { CiBadge } from '@/features/dev/ci-badge';
import { ConflictBadge } from '@/features/dev/conflict-badge';
import { MergeBox } from '@/features/dev/merge';
import { ReviewBox } from '@/features/dev/review';
import { Timeline } from '@/features/dev/timeline';

export const Route = createFileRoute('/_app/dev/pulls/$id')({ component: PullDetail });

const stateTone = { open: 'success', merged: 'info', closed: 'neutral' } as const;
const verdictTone = { approved: 'success', changes_requested: 'danger', commented: 'outline', pending: 'outline', requested: 'outline', dismissed: 'neutral' } as const;

/**
 * One pull request (CO-0420): body, reviews with their verdicts (CO-0421), the CI of its head (CO-0416) and the
 * issues it closes or mentions (CO-0419), and its activity (CO-0551). Reviewing stays in Forgejo; a person with dev:approve
 * merges here with their own account (CO-1238).
 */
function PullDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const q = useQuery({ queryKey: ['dev', 'pull', id], queryFn: () => call(getPull, { id }) });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const p = q.data;
  return (
    <>
      <Link to="/dev/pulls" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('pulls.title')}
      </Link>
      <PageHeader
        eyebrow={
          <Mono className="text-xs">
            {p.repository}#{p.number}
          </Mono>
        }
        title={p.title}
        description={
          <span className="inline-flex flex-wrap items-center gap-2 text-sm">
            <Badge tone={stateTone[p.state]}>{t(`pulls.state.${p.state}`)}</Badge>
            {p.draft && <Badge tone="outline">{t('pulls.draft')}</Badge>}
            <CiBadge state={p.ciState} />
            <ConflictBadge mergeable={p.mergeable} showClean />
            {p.author && t('pulls.openedBy', { author: p.author, at: formatDateTime(p.createdAt) })}
            {p.mergedAt && t('pulls.mergedBy', { by: p.mergedBy ?? '—', at: formatDateTime(p.mergedAt) })}
            <a href={p.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
              {t('issues.inForge')} <ExternalLink className="size-3" />
            </a>
          </span>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
        <div className="grid content-start gap-3">
          <Card className="p-4">{p.body ? <Markdown text={p.body} base={p.url} /> : <p className="text-sm text-fg-3">{t('issues.noBody')}</p>}</Card>
          <h2 className="text-lg font-bold">{t('pulls.reviewsTitle', { count: p.reviews.length })}</h2>
          {p.reviews.map((r) => (
            <Card key={r.id} className="p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <strong>{r.reviewer ?? '—'}</strong>
                <Badge tone={verdictTone[r.state]}>{t(`pulls.verdict.${r.state}`)}</Badge>
                {r.stale && <Badge tone="outline">{t('pulls.stale')}</Badge>}
                {r.submittedAt && <span className="text-xs text-fg-3">{formatDateTime(r.submittedAt)}</span>}
              </div>
              {r.body && <Markdown text={r.body} base={p.url} className="mt-2" />}
            </Card>
          ))}
          <ReviewBox pull={p} />
          <Timeline pullRequestId={id} />
        </div>
        <aside className="grid content-start gap-4 text-sm">
          <MergeBox pull={p} />
          <div>
            <h3 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('pulls.branches')}</h3>
            <Mono className="text-xs">
              {p.headRef ?? '—'} → {p.baseRef ?? '—'}
            </Mono>
            {p.headSha && <div className="mt-1 text-xs text-fg-3">{p.headSha.slice(0, 10)}</div>}
          </div>
          <div>
            <h3 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('pulls.ci')}</h3>
            {p.statuses.length ? (
              <ul className="grid gap-1">
                {p.statuses.map((s) => (
                  <li key={s.context} className="flex items-center justify-between gap-2">
                    {s.targetUrl ? (
                      <a href={s.targetUrl} target="_blank" rel="noreferrer" className="truncate underline">
                        {s.context}
                      </a>
                    ) : (
                      <span className="truncate">{s.context}</span>
                    )}
                    <CiBadge state={s.state} />
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-fg-3">{t('pulls.noCi')}</span>
            )}
          </div>
          <div>
            <h3 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('pulls.issues')}</h3>
            {p.issues.length ? (
              <ul className="grid gap-1">
                {p.issues.map((i) => (
                  <li key={i.id}>
                    <Link to="/dev/issues/$id" params={{ id: i.id }} className="hover:underline">
                      #{i.number} {i.title}
                    </Link>{' '}
                    <span className="text-xs text-fg-3">{t(`links.${i.kind}`)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              '—'
            )}
          </div>
          <div>
            <h3 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('pulls.people')}</h3>
            {p.assignees.length ? t('pulls.assigned', { names: p.assignees.join(', ') }) : null}
            {p.requestedReviewers.length > 0 && <div>{t('pulls.waitsFor', { names: p.requestedReviewers.join(', ') })}</div>}
            {!p.assignees.length && !p.requestedReviewers.length && '—'}
          </div>
          {p.commits > 0 && <p className="text-xs text-fg-3">{t('pulls.commits', { count: p.commits })}</p>}
          <p className="text-xs text-fg-3">{t('issues.synced', { at: formatDateTime(p.syncedAt) })}</p>
        </aside>
      </div>
    </>
  );
}
